import { signInFormAction } from '@/app/form-actions/auth';
import { setFormErrors } from '@/utils/form';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useFormState, useFormStatus } from 'react-dom';
import { motion } from 'motion/react';
import { FieldValues, SubmitHandler, useForm, UseFormReturn } from 'react-hook-form';
import { FiArrowRight } from 'react-icons/fi';
import { CxButton } from '../atoms/CxButton';
import { CxInputForm } from '../atoms/CxInput';
import { WelcomeToCxMapper } from '../atoms/WelcomeToCxMapper';
import ErrorLabel from '../atoms/ErrorLabel';
import { getRecaptchaToken } from '@/utils/recaptcha';
import { load } from 'recaptcha-v3';
import { zodResolver } from '@hookform/resolvers/zod';
import { signInSchema } from '@/schemas/auth';
import { z } from 'zod';
import { bounceTransition } from '@/animation/transitions';

interface FormContentProps {
  form: UseFormReturn<FieldValues, any, undefined>;
  isLoading: boolean;
  clientError: string | null;
}

function FormContent({ form, isLoading, clientError }: FormContentProps) {
  const status = useFormStatus();

  return (
    <>
      <WelcomeToCxMapper />

      <div className="mt-5 flex flex-col gap-4">
        <CxInputForm
          control={form.control}
          name="email"
          label="E-mail"
          placeholder="Digite seu email"
          type="email"
          inputMode="email"
          autoComplete="email"
          disableAutocomplete={false}
        />
      </div>

      {clientError && <ErrorLabel className="mt-4">{clientError}</ErrorLabel>}

      <div className="mt-4">
        <CxButton
          label="Entrar"
          isLoading={status.pending || isLoading}
          icon={<FiArrowRight className="text-gray-02" />}
          type="submit"
        />
      </div>
    </>
  );
}

type FormData = z.output<typeof signInSchema>;

export function SignInForm() {
  const formRef = useRef<HTMLFormElement>(null);
  const [actionState, formAction] = useFormState(signInFormAction, {});
  const [isLoading, setIsLoading] = useState(false);
  const [clientError, setClientError] = useState<string | null>(null);
  const [isRecaptchaReady, setIsRecaptchaReady] = useState(false);
  const recaptchaSiteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY?.trim() ?? '';

  const form = useForm<FormData>({
    resolver: zodResolver(signInSchema),
  });

  const onSubmit: SubmitHandler<FormData> = useCallback(async () => {
    setIsLoading(true);
    setClientError(null);

    if (!recaptchaSiteKey || !isRecaptchaReady) {
      setClientError('Não foi possível validar o reCAPTCHA. Recarregue a página e tente novamente.');
      setIsLoading(false);
      return;
    }

    try {
      const token = await getRecaptchaToken('signin');

      if (!formRef.current) {
        setIsLoading(false);
        return;
      }

      const formData = new FormData(formRef.current);
      formData.append('recaptchaToken', token);

      formAction(formData);
    } catch (error) {
      console.error('Erro ao executar reCAPTCHA:', error);
      setClientError('Não foi possível validar o reCAPTCHA. Recarregue a página e tente novamente.');
      setIsLoading(false);
    }
  }, [formAction, isRecaptchaReady, recaptchaSiteKey]);

  useEffect(() => {
    setIsLoading(false);
    setFormErrors(actionState?.fieldErrors, form as any);
  }, [actionState, form]);

  useEffect(() => {
    if (!recaptchaSiteKey) {
      console.error('NEXT_PUBLIC_RECAPTCHA_SITE_KEY não está configurada.');
      setIsRecaptchaReady(false);
      return;
    }

    load(recaptchaSiteKey, { autoHideBadge: false })
      .then(() => {
        setIsRecaptchaReady(true);
      })
      .catch((error) => {
        console.error('Erro ao carregar reCAPTCHA:', error);
        setIsRecaptchaReady(false);
      });
  }, [recaptchaSiteKey]);

  return (
    <motion.form
      ref={formRef}
      action={formAction}
      onSubmit={form.handleSubmit(onSubmit)}
      animate={{ opacity: 1, translateX: 0 }}
      exit={{ opacity: 0, translateX: 100 }}
      transition={bounceTransition}
    >
      {actionState?.error && <ErrorLabel className="mb-4">{actionState?.error}</ErrorLabel>}
      <FormContent form={form as any} isLoading={isLoading} clientError={clientError} />
    </motion.form>
  );
}
